import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  AddTenantMembersDocument,
  type AddTenantMembersMutation,
  type AddTenantMembersMutationVariables,
  GetTenantDocument,
} from "~/features/admin/tenants/gqls";
import { translateError } from "~/lib/errors";

interface UseTenantAddMembersOptions {
  onSuccess?: () => void;
}

/** Add users to a tenant. */
export function useTenantAddMembers({ onSuccess }: UseTenantAddMembersOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [addMembers] = useMutation<AddTenantMembersMutation, AddTenantMembersMutationVariables>(
    AddTenantMembersDocument,
    { refetchQueries: [GetTenantDocument], awaitRefetchQueries: true },
  );

  const submit = useCallback(
    async (tenantId: string, userIds: string[]) => {
      setProcessing(true);
      try {
        const res = await addMembers({ variables: { input: { id: tenantId, userIds } } });
        match(res.data?.cnutAddTenantMembers)
          .with({ __typename: "ValidationError" }, (e) => toast.error(e.fields.map((f) => f.message).join(", ")))
          .with({ __typename: "TenantAddMembersOutput" }, () => {
            toast.success(t("model.tenant.membersAddedSuccessfully"));
            onSuccess?.();
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [addMembers, t, onSuccess],
  );

  return { submit, processing };
}
